import React, {useState, useEffect, useCallback} from "react";
import PropTypes from "prop-types";
import {useSelector, useDispatch, connect} from "react-redux";
import {
    initializeEditorCreate,
    initializeEditorUpdate,
    updateTemplate,
    clearEditor,
    validateActionNameUnique
} from "../../../actions/actionEditorActions";
import { getActionTemplate, clearActionTemplate } from "../../../actions/actionTemplateActions";
import ConfirmPromptHook from "../../dialogs/ConfirmPromptHook";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import * as validate from "../../../utilities/validate";
import ArgoTextFieldSelectHook from "../../common/ArgoTextFieldSelectHook";
import {bindActionCreators} from "redux";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";
import QuestionViewerExpandableHook from "../questions/QuestionViewerExpandableHook";
import * as TEMPLATE from "../../../constants/templateConstants";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";

function ActionEditor(props) {
    const [openQuestionReview, setOpenQuestionReview] = useState(false);
    const [selectedQuestionInfoId, setSelectedQuestionInfoId] = useState("");
    const [selectedQuestionInfoKey, setSelectedQuestionInfoKey] = useState("");
    const [selectedQuestionInfoTemplate, setSelectedQuestionInfoTemplate] = useState("");
    const [nameErrorText, setNameErrorText] = useState(" ");
    const [actionTypeErrorText, setActionTypeErrorText] = useState(" ")
    const [isLoading, setIsLoading] = useState(false);
    const [toolbarErrorText, setToolbarErrorText] = useState("");
    const [openActionNameEditDialog, setOpenActionNameEditDialog] = useState(false);
    const [originalName, setOriginalName] = useState(props.template?.name || "");
    const [scrollToTop, setScrollToTop] = useState(false);

    const dispatch = useDispatch();
    
    // Redux state selectors
    const template = useSelector(state => state.actionEditor.template);
    const options = useSelector(state => state.actionEditor.options);
    const actionTypeList = useSelector(state => state.actionTypeList);
    const actionTemplate = useSelector(state => state.actionTemplate);
    const templateOriginal = useSelector(state => state.actionTemplate[props.templateKey]);

    useEffect(() => {
        dispatch(addDrawerNameToStack(props.createMode ? "Create" : "Edit", "Action"))

        // componentWillUnmount equivalent
        return () => {
            dispatch(removeDrawerNameToStack())
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    // Equivalent to componentDidMount
    useEffect(() => {
        if (props.templateKey || props.templateId) {
            if (actionTemplate[props.templateKey] && (actionTemplate[props.templateKey].id === props.templateId)) {
                dispatch(initializeEditorUpdate(actionTemplate[props.templateKey]));
            } else if (props.templateKey) {
                setIsLoading(true);
                dispatch(getActionTemplate(props.templateKey, props.templateId));
            }
        } else {
            dispatch(initializeEditorCreate());
        }
        
        // Cleanup function (componentWillUnmount)
        return () => {
            dispatch(clearActionTemplate());
            dispatch(clearEditor());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, props.templateKey, props.templateId]);

    // Equivalent to componentWillReceiveProps
    useEffect(() => {
        // if templateOriginal is loaded and the templateKey matches the current templateKey and is different from the loaded template
        const isTemplateLoaded = (templateOriginal && templateOriginal.templateKey === props.templateKey && template.templateKey !== props.templateKey);

        // The loaded template is stale and the correct template is loaded
        const editTemplateReady = template.id !== props.templateId &&
            (actionTemplate[props.templateKey] && actionTemplate[props.templateKey].id === props.templateId);

        if (isTemplateLoaded && editTemplateReady) {
            setIsLoading(false);
            setOriginalName(templateOriginal.name);
            dispatch(initializeEditorUpdate(templateOriginal));
        }

        checkForErrorsOnScreen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, templateOriginal, actionTemplate, props.templateKey, props.templateId, props.options]);

    useEffect(() => {
        const page = document.getElementById(props.scrollTo === "" ? "ActionEditor-height-div" : props.scrollTo);
        if (page && !scrollToTop) {
            page.scrollIntoView({block: 'end', behavior: 'smooth'});
            setScrollToTop(true);
        }
    }, [props.scrollTo, template, scrollToTop]);

    const handleToolbarButtonClick = (event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "REVIEW":
                // Not implemented in original component
                break;
            case "CANCEL":
                props.cancel();
                break;
            case "SAVE":
                const templateToSave = { ...template };
                props.save(templateToSave);
                break;
            case "BACK":
                // Not implemented in original component
                setOpenQuestionReview(false);
                break;
            // no default
        }
        event.preventDefault(); // Fix for the enter key propagating to the next focusable element
    };

    const onTextChange = (event, fieldName) => {
        const value = event.target.value;
        handleChange(event, fieldName, value);
    };

    const onSelectChange = (event, fieldName, menuItemText) => {
        const value = event.target.value;
        handleChange(event, fieldName, value);
    };

    const handleChange = (event, fieldName, value) => {
        const updatedTemplate = { ...template };
        updatedTemplate[fieldName] = value;

        dispatch(updateTemplate(updatedTemplate));

        if (fieldName === "name") {
            // Disable the review button when the header changes. It will be re-enabled once the validation is complete.
            dispatch(validateActionNameUnique({name: updatedTemplate.name}));
        }
    };

    const onBlurActionName = () => {
        const isEdit = template.templateKey !== "";

        if (isEdit && !isLoading && originalName !== "") {
            if (template.name !== originalName) {
                setOpenActionNameEditDialog(true);
            }
        }
    };

    const checkForErrorsOnScreen = () => {
        const nameError = validate.templateNameUnique(props, "Action Name");
        let actionTypeError = props.template.type === "" ? "Action Type is required" : " "
        let toolbarError = "";

        if (nameError !== " ") {
            toolbarError = nameError;
        }

        if (actionTypeError !== " ") {
            toolbarError = toolbarError === "" ? actionTypeError : toolbarError + ", " + actionTypeError;
        }

        setNameErrorText(nameError);
        setActionTypeErrorText(actionTypeError)
        setToolbarErrorText(toolbarError);
    };

    const handleCloseActionNameEditCancel = () => {
        setOpenActionNameEditDialog(false);
        dispatch(clearEditor());
    };

    const handleCloseActionNameEditConfirm = () => {
        setOpenActionNameEditDialog(false);
    };

    const handleQuestionViewerStateChange = useCallback((stateObj) => {
        if ('openQuestionReview' in stateObj) setOpenQuestionReview(stateObj.openQuestionReview);
        if ('selectedQuestionInfoId' in stateObj) setSelectedQuestionInfoId(stateObj.selectedQuestionInfoId);
        if ('selectedQuestionInfoKey' in stateObj) setSelectedQuestionInfoKey(stateObj.selectedQuestionInfoKey);
        if ('selectedQuestionInfoTemplate' in stateObj) setSelectedQuestionInfoTemplate(stateObj.selectedQuestionInfoTemplate);
    }, []);

    if (isLoading) {
        // TODO add a loading screen here
        return null;
    }

    if (openQuestionReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.QUESTION}
            templateId={selectedQuestionInfoId}
            templateKey={selectedQuestionInfoKey}
            selectedTemplate={selectedQuestionInfoTemplate}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }

    return (
        <div id="ActionEditor-page-container-div" style={{ height: "100%" }}>
            <ArgoToolbarHook
                fileName="ActionEditor"
                fieldName="save-or-cancel"
                toolbarErrorText={toolbarErrorText}
                isValidating={options.isValidating}
                width={props.width}
                onClick={handleToolbarButtonClick}
            />
            <div id="ActionEditor-height-div" style={{ height: "50px" }}></div>

            <div id="ActionEditor-container-div1" style={{ height: "100%" }}>
                <div id="ActionEditor-container-div2"
                    style={{ flexGrow: "2", paddingLeft: "32px", paddingRight: "32px" }}>

                    <div id="ActionEditor-spacer-div1" style={{ height: "10px" }}>&nbsp;</div>

                    <ArgoTextFieldHook
                        fileName="ActionEditor"
                        fieldName="name"
                        label="Name"
                        value={template.name}
                        onChange={onTextChange}
                        onBlur={onBlurActionName}
                        maxLength={255}
                        multiline={true}
                        errorText={nameErrorText}
                        autoFocus={true}
                    />

                    <div>&nbsp;</div>

                    <ArgoTextFieldHook
                        fileName="ActionEditor"
                        fieldName="description"
                        label="Description/Instructions"
                        value={template.description}
                        onChange={onTextChange}
                        maxLength={255}
                        multiline={true}
                    />

                    <div>&nbsp;</div>

                    <ArgoTextFieldSelectHook
                        fileName="ActionEditor"
                        fieldName="type"
                        label="Action type"
                        value={template.type}
                        onChange={onSelectChange}
                        menuItems={actionTypeList}
                        useTextForValue={true}
                        errorText={actionTypeErrorText}
                    />

                    {/*// ===========================================================================================================*/}
                    {/*// ASSIGNED QUESTIONS LIST */}
                    {/*// ===========================================================================================================*/}

                    {!props.createMode ?
                        <QuestionViewerExpandableHook
                            parentName={"ActionEditor"}
                            parentType={TEMPLATE.TYPE.ACTION}
                            parentTemplate={template}
                            questionKeys={template.questionKeys ? template.questionKeys : []}
                            showIconButtonReorder={false}
                            showIconButtonEdit={false}
                            showIconButtonRemove={false}
                            setStateCallback={handleQuestionViewerStateChange}
                            // setStateCallbackRemovedQuestionListUpdate={handleQuestionRemoved}
                        /> : ""
                    }

                    <ConfirmPromptHook
                        open={openActionNameEditDialog}
                        title={"Changing the action name may have unintended consequences."}
                        message={"Any answer options that this action is associated with in the system will now have a different meaning. " +
                            "This will also affect the admin when attempting to select the action from a list. " +
                            "Are you sure you want to do this?"}
                        ok={handleCloseActionNameEditConfirm}
                        cancel={handleCloseActionNameEditCancel}
                    />
                </div>
            </div>
        </div>
    );
}

ActionEditor.defaultProps = {
    createMode: false,
    width: "100%",
    scrollTo: "",
};

ActionEditor.propTypes = {
    scrollTo: PropTypes.string,
    createMode: PropTypes.bool,
    templateKey: PropTypes.string,
    templateId: PropTypes.number,
    save: PropTypes.func,
    cancel: PropTypes.func,
    showCreateToggle: PropTypes.func,
    width: PropTypes.string
};

function mapStateToProps(state, props) {
    return {
        template: state.actionEditor.template,
        options: state.actionEditor.options,
        actionTypeList: state.actionTypeList,
        actionTemplate: state.actionTemplate,
        templateOriginal: state.actionTemplate[props.templateKey]
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        initializeEditorCreate,
        initializeEditorUpdate,
        updateTemplate,
        clearEditor,
        clearActionTemplate,
        getActionTemplate,
        validateActionNameUnique
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionEditor);